// JOBE-0098.3: top-level <OmniPalette>. Owns the open/closed local
// state, exposes an imperative handle (open / close / isOpen) so the
// SPA shell (JOBE-0098.5) can wire the chord registry against it, and
// renders SearchTrigger or PalettePanel based on the open flag.
//
// The open panel is portaled to <body> over a scrim, mirroring the
// existing CommandPalette pattern in Overview.jsx. On close, focus is
// restored to whichever element opened the palette (the collapsed
// trigger, or — when the shell opens via chord — the previously
// focused element).

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SearchTrigger } from './SearchTrigger.jsx';
import { PalettePanel } from './PalettePanel.jsx';
import { useRecentQueries } from './useRecentQueries.js';
import './OmniPalette.css';

export const OmniPalette = forwardRef(function OmniPalette({
  placeholderVariant = 'library',
  sources,
  onApplyFilters,
  onSaveView,
  onRunAsk,
  onNavigate,
  onSelectRow,
  onSearch,
  onClose,
}, ref) {
  const [open, setOpen] = useState(false);
  // Recents live HERE (not inside PalettePanel) so they survive the
  // open→close→reopen cycle without remount-induced state loss. The
  // OmniPalette.test recents-persistence test depends on this ordering:
  // ⇧↩ calls onClose synchronously, then ref.current.open() reopens
  // immediately, and the new mount of PalettePanel reads recents from
  // this hook. Moving the hook into PalettePanel would race that test.
  const { recents, push } = useRecentQueries();
  const triggerRef = useRef(null);
  // Capture whichever element was focused at open() time so we can
  // restore focus on close even when the shell opens us via chord
  // (no trigger click).
  const returnFocusRef = useRef(null);
  // Mirror `open` into a ref so the imperative handle's `isOpen`
  // accessor reads the current value without being re-bound on every
  // open/close transition. See note on useImperativeHandle below.
  const openRef = useRef(false);
  openRef.current = open;

  const openPalette = useCallback(() => {
    if (typeof document !== 'undefined') {
      returnFocusRef.current = document.activeElement;
    }
    setOpen(true);
  }, []);

  const closePalette = useCallback(() => {
    setOpen(false);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    if (onClose) onClose();
  }, [onClose]);

  // Stable handle identity: the returned object never changes across
  // renders. Accessors close over stable callbacks (open/close are
  // useCallback with [] deps) and a ref for `open`. This prevents
  // register/unregister flap on consumers that key effects on
  // `ref.current` identity (e.g. the JOBE-0098.5 chord registry).
  useImperativeHandle(ref, () => ({
    open: openPalette,
    close: closePalette,
    isOpen: () => openRef.current,
  }), [openPalette, closePalette]);

  // Restore focus on transitions to closed. Prefer the captured
  // returnFocusRef (works for shell-chord opens); fall back to the
  // collapsed trigger button (works for click-to-open). Uses a ref
  // to detect the open→closed transition without re-firing on mount.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      return;
    }
    if (!wasOpenRef.current) return;
    wasOpenRef.current = false;
    const target = returnFocusRef.current;
    if (target && typeof target.focus === 'function' && document.contains(target)) {
      target.focus();
      return;
    }
    if (triggerRef.current) triggerRef.current.focus();
  }, [open]);

  const trigger = (
    <SearchTrigger
      ref={triggerRef}
      placeholderVariant={placeholderVariant}
      onOpen={openPalette}
    />
  );

  if (!open) return trigger;

  const panel = (
    <div
      className="omni-scrim"
      role="presentation"
      onMouseDown={(e) => {
        // Backdrop click closes; clicks inside the panel stopPropagation.
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="omni-portal-host" onMouseDown={(e) => e.stopPropagation()}>
        <PalettePanel
          placeholderVariant={placeholderVariant}
          sources={sources}
          recents={recents}
          onPushRecent={push}
          onApplyFilters={onApplyFilters}
          onSaveView={onSaveView}
          onRunAsk={onRunAsk}
          onNavigate={onNavigate}
          onSelectRow={onSelectRow}
          onSearch={onSearch}
          onClose={close}
        />
      </div>
    </div>
  );

  return (
    <>
      {trigger}
      {typeof document !== 'undefined'
        ? createPortal(panel, document.body)
        : panel}
    </>
  );
});
